import axios from "axios";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Col, Container, Dropdown, Row, Stack } from "react-bootstrap";
import { useCookies } from "react-cookie";
import { useQuery } from "react-query";
import { useAuth } from "../hooks/useAuth";
import { useDisclaimer } from "../hooks/useDisclaimer";
import { useEnvironment } from "../hooks/useEnvironment";
import { useShowStickyOnMount } from "../hooks/useStikyMessage";
import { getFilePath } from "../queries/products";
import { ProductDefinition, ProductInfo } from "../types/Product";
import { PageWrap } from "./PageWrap";
import ProductChekboxFilter from "./list/ProductChekboxFilter";
import { ProductListTableItems } from "./list/ProductListTableItems";
import { ProductsChart } from "./list/ProductsChart";
import RankSidebar from "./list/RelevanceSorter";
import { Loader } from "../components/Loader";

export const tenYearsReturnH = "ten_years_perf";
export const tenYearsReturnDiff = "ten_years_perf_diff";
export const oneYearReturn = "oneYearPerf";
export const oneYearReturnDiff = "";
export const threeYearsReturn = "three_years_perf";
export const threeYearsReturnDiff = "";
export const fiveYearsReturn = "";
export const fiveYearsReturnDiff = "";
export const sharpeRatio = "sharpeRatio";
export const avgYearlyDrawDown = "drawDown";
export const ytd = "";
export const ytdDiff = "";
export const turnover = "turnoverAnalytic";

type PerformanceChartListProps = {
  products: any;
  filter: any;
  setTableSorter: (value: any) => void;
  tableSorter: any;
  showChart: boolean;
  setShowChart: (val: any) => void;
};

// Beware of upper/lower case
export const orderedKeys = {
  capture: ["US", "Europe", "Asia"],
  geo: ["us", "eurozone", "europe", "usandeurope", "asia"],
  theme: ["growth", "value", "growthvalue", "trend"],
  turnover: ["high", "low"],
  method: ["longonly", "longshort"],
  rebalance: ["weekly", "monthly", "quarterly"],
  type: ["product", "combined-products"],
  uiLevels: ["primary", "expanded"],
};

export const sortByStructure = (a: string, b: string, list: string[]) => {
  let numberA = list.indexOf(a);
  if (numberA === -1) {
    numberA = Infinity;
  }
  let numberB = list.indexOf(b);
  if (numberB === -1) {
    numberB = Infinity;
  }
  if (numberA < numberB) {
    return -1;
  } else if (numberA > numberB) {
    return 1;
  } else {
    return 0;
  }
};

export const sortNumeric = (a: string, b: string) => {
  const numberA = parseInt(a, 10);
  const numberB = parseInt(b, 10);
  if (numberA < numberB) {
    return -1;
  } else if (numberA > numberB) {
    return 1;
  } else {
    return 0;
  }
};

export const getOrderedKeys = (obj: any, id: string): any[] => {
  if (obj == null) {
    return [];
  }

  const keys = Object.keys(obj);

  switch (id) {
    case "geo":
    case "turnover":
    case "theme":
    case "rebalance":
      keys.sort((a, b) => sortByStructure(a, b, orderedKeys[id]));
      break;
    case "positions":
      keys.sort((a, b) => sortNumeric(a, b));
      break;
  }

  return keys;
};

const useProductsFilters = (filter: any, products: any, setFilter: any) => {
  // Returns a function to sort by a specific list of strings
  const sortByStructure = (list: string[]) =>
    function (a: string, b: string) {
      let numberA = list.indexOf(a);
      if (numberA === -1) {
        numberA = Infinity;
      }
      let numberB = list.indexOf(b);
      if (numberB === -1) {
        numberB = Infinity;
      }
      if (numberA < numberB) {
        return -1;
      } else if (numberA > numberB) {
        return 1;
      } else {
        return 0;
      }
    };

  const filterProductByMethod = useCallback(
    (product: ProductInfo): boolean => {
      // Explicity ignore filter if length is 0
      if ((filter?.method?.length ?? 0) > 0) {
        return filter.method.includes(product.method);
      }
      return true;
    },
    [filter],
  );

  const filterProductByGeo = useCallback(
    (product: ProductInfo): boolean => {
      // Explicity ignore filter if length is 0
      if ((filter?.geo?.length ?? 0) > 0) {
        return filter.geo.includes(product.geo);
      }
      return true;
    },
    [filter],
  );

  // Build
  const geoList: string[] = useMemo(() => {
    const geoList: any = Array.from(
      new Set(
        products
          .filter(filterProductByMethod)
          .map((product: any) => product.geo)
          .filter(Boolean)
          .sort(sortByStructure(orderedKeys.geo)),
      ),
    );

    // geoList.push("any");

    return geoList;
  }, [filterProductByMethod, products]);

  const themeList = useMemo(
    () =>
      Array.from(
        new Set(
          products
            .filter(filterProductByMethod)
            .filter(filterProductByGeo)
            .map((product: any) => product.theme)
            .filter(Boolean)
            .sort(sortByStructure(orderedKeys.theme)),
        ),
      ),
    [filterProductByGeo, filterProductByMethod, products],
  );

  useEffect(() => {
    // Check if there is a theme filter active that is not anymore inside
    // the themeList array
    let resetThemeFilter = false;
    let validThemeFilters: string[] = [];
    for (const themeFilter of filter.theme) {
      if (!themeList.includes(themeFilter)) {
        resetThemeFilter = true;
      } else {
        validThemeFilters.push(themeFilter);
      }
      if (resetThemeFilter) {
        setFilter((prevFilter: any) => ({
          ...prevFilter,
          theme: validThemeFilters,
        }));
      }
    }
  }, [filter.theme, themeList, setFilter]);

  // Do NOT filter by method! It will remove the other options
  const methodList = useMemo(() => {
    const methods: any = Array.from(
      new Set(
        products
          .map((product: any) => product.method)
          .filter(Boolean)
          .sort(sortByStructure(orderedKeys.method)),
      ),
    ).filter(Boolean);

    return methods;
  }, [products]);

  const rebalanceList = useMemo(() => {
    const rebalnceOpts: any = Array.from(
      new Set(
        products
          .map((product: any) => product.rebalance)
          .filter(Boolean)
          .sort(sortByStructure(orderedKeys.rebalance)),
      ),
    ).filter(Boolean);

    return rebalnceOpts;
  }, [products]);

  const uiLevels = useMemo(() => {
    const levelsOptions: any = Array.from(
      new Set(
        products
          .map((product: any) => product.uiLevel)
          .filter(Boolean)
          .sort(sortByStructure(orderedKeys.uiLevels)),
      ),
    ).filter(Boolean);

    return levelsOptions;
  }, [products]);

  return products && products.length
    ? { rebalanceList, geoList, methodList, uiLevels }
    : {
        rebalanceList: undefined,
        geoList: undefined,
        methodList: undefined,
        uiLevels,
      };
};

export const ProductListNew = () => {
  const checkDisclaimer = useDisclaimer();
  const auth = useAuth();
  const { environment } = useEnvironment();
  const [cookie, setCookie] = useCookies(["productFilters"]);
  const [tableSorter, setTableSorter] = useState({
    field: "oneYearPerf",
    dir: "desc",
  });
  const [showChart, setShowChart] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const [showMethodolgyBox, setShowMethodolgyBox] = useState(false);

  const initState = useMemo(() => {
    const defaultState: any = {
      geo: [],
      method: ["longonly"],
      theme: [],
      positions: [],
      rebalance: [],
      uiLevel: ["primary"],
      type: [],
    };
    const savedState = cookie?.productFilters;

    // Maybe the cookie has not a new filter so add the key to prevent app to crash
    if (savedState) {
      for (const key in defaultState) {
        if (!(key in savedState)) {
          savedState[key] = defaultState[key];
        }
      }

      return savedState;
    } else {
      return defaultState;
    }
  }, [cookie?.productFilters]);

  const [filter, setFilter] = useState(initState);

  useShowStickyOnMount();

  useEffect(() => {
    setCookie("productFilters", filter);
  }, [filter, setCookie]);

  const localhostFile = environment.api.devModeProducts;
  const filepath = getFilePath(localhostFile);

  const {
    isLoading: isLoadingList,
    error: errorList,
    data: dataList,
  } = useQuery<any, Error>("products", () => {
    return axios(`${environment.api.uri + filepath}`);
  });

  const products: ProductInfo[] = useMemo(() => {
    if (dataList == null) {
      return [];
    }

    const combinedProducts: ProductDefinition[] = dataList.data.filter(
      (item: ProductDefinition) =>
        item.type === "product" || item.type === "combined-product",
    );

    return combinedProducts.map((combinedProduct) => {
      const product: ProductInfo = {
        analytics: combinedProduct.analytics,
        exposure: combinedProduct.info.exposure ?? null,
        id: combinedProduct.name,
        name: combinedProduct.info.name,
        geo: combinedProduct.info.geo?.toLowerCase() as ProductInfo["geo"],
        method: (combinedProduct.info.method?.toLowerCase() ??
          "longonly") as ProductInfo["method"],
        rebalance: combinedProduct.info.rules.rebalance[0],
        theme:
          combinedProduct.info.theme?.toLowerCase() as ProductInfo["theme"],
        turnover:
          combinedProduct.info.turnover?.toLowerCase() as ProductInfo["turnover"],
        positions: combinedProduct.info.positions,
        type: combinedProduct.type,
        description: combinedProduct.info.description,
        rules: combinedProduct.info.rules,
        title: combinedProduct.info.title,
        uiLevel: combinedProduct.info.uiLevel,
        strategyAnalytics: {
          priceAnalytics: combinedProduct?.priceAnalytics?.stats ?? null,
          hPosAnalytics: combinedProduct?.hPosAnalytics?.stats ?? null,
        },
      };
      return product;
    });
  }, [dataList]);

  const { methodList, geoList, rebalanceList, uiLevels } = useProductsFilters(
    filter,
    products,
    setFilter,
  );

  if (isLoadingList) {
    return (
      <PageWrap>
        <div className="pb-3 mb-5">
          <Container>
            <div>
              <Loader message="Loading..." showMessage={true} fullPage />
            </div>
          </Container>
        </div>
      </PageWrap>
    );
  }

  if (errorList) {
    return <PageWrap>Error: {errorList.message}</PageWrap>;
  }

  if (!auth.isReady) {
    return <PageWrap>Check user...</PageWrap>;
  }

  return (
    <PageWrap
      childrenStartFromLeft
      headerChildren={
        <h1 className="header__title__children">SYSTEMATIC MODEL PORTFOLIOS</h1>
      }
    >
      {checkDisclaimer}
      <div className="head-content">
        <Container>
          <Row>
            <Col lg={9}>
              <Row>
                <Col lg={12}>
                  <span
                    className="head-content__intro"
                    style={{
                      lineHeight: 1.4,
                    }}
                  >
                    Designed to profit from the{" "}
                    <span className="accent">performance dispersion</span> and
                    to select the{" "}
                    <span className="accent">best performing stocks</span> using
                    a well-tested combination of the most rewarding fundamental
                    parameters and a factual validation of positive price trends
                    in action.
                  </span>
                </Col>
              </Row>
              <Row className="d-none d-md-flex">
                <Col lg={12}>
                  <p
                    style={{
                      fontSize: "0.85rem",
                      color: "rgba(0,0,0,0.45)",
                      marginTop: "10px",
                    }}
                  >
                    The model portfolios performance is price only, and does not
                    incorporate the dividend income.
                  </p>
                </Col>
              </Row>
            </Col>
            <Col lg={3} className="d-none d-md-block">
              <HelpRow
                setShowChart={setShowChart}
                showMethodolgyBox={showMethodolgyBox}
                showWarning={showWarning}
                setShowWarning={setShowWarning}
                setShowMethodolgyBox={setShowMethodolgyBox}
                showChart={showChart}
              />
            </Col>
          </Row>

          {showMethodolgyBox && (
            <Row>
              <Col sm={12}>
                <h3>
                  <strong>Methodology </strong>
                </h3>
                <div className="my-4">
                  <table
                    style={{
                      width: "100%",
                      borderSpacing: "10px",
                    }}
                  >
                    <tbody>
                      <tr>
                        <td>Long Only</td>
                        <td>long 20 to 40 socks</td>
                      </tr>
                      <tr>
                        <td>Long & Short</td>
                        <td>long 30 stocks and short 30 stocks</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <h4>
                  <strong>Long only</strong>
                </h4>
                <p>
                  This model selects the best 20 t0 40 stocks combining the most
                  productive fundamentals and the validation of a positive price
                  trend. The portfolio is adjusted at the end of each month or
                  quarter to always hold only the very best stocks.
                </p>
                <h4>
                  <strong>Long and short</strong>
                </h4>
                <p>
                  These models work on a long and a short exposure, with
                  different weights. The long position selects the best 30
                  stocks combining the most productive fundamentals and the
                  validation of a positive price trend. The short position picks
                  30 stocks with poor fundamentals and a negative trend. The
                  model can readjust the positions at the end of the month.
                </p>
                <Col sm={12} style={{ textAlign: "right" }}>
                  <p
                    style={{ cursor: "pointer" }}
                    onClick={() => setShowMethodolgyBox(false)}
                  >
                    <strong>Close</strong>
                  </p>
                </Col>
                {showWarning && (
                  <div
                    style={{
                      height: "1px",
                      width: "100%",
                      background: "white",
                      marginBottom: "20px",
                    }}
                  ></div>
                )}
              </Col>
            </Row>
          )}
          {showWarning && (
            <Row>
              {" "}
              <Col sm={12} className="mb-2">
                <p className="">
                  <strong className="d-block">
                    Model portfolios present several risks
                  </strong>
                  While a model portfolio tends to deliver superior returns on a
                  medium-to-long term basis, it may well record a sequence of
                  negative months either in absolute performance as well as in
                  terms of differential vs. the benchmark. The drawdowns can be
                  substantial and even exceed the benchmark drawdowns. A
                  long-term perspective is required to make sense of model
                  portfolios. Please familiarize with the statistics in the
                  “historical performance” section of any portfolio and double
                  check if the risk/return key metrics fit your investment
                  profile. Look at the “chart” section to analyze the
                  distribution of quarterly returns across the last 10 years .
                  It is also possible to combine two portfolios to diversify the
                  exposure. Some model portfolios may require a high turn-over
                  in order to maximize the performance.
                </p>
                <Col sm={12} style={{ textAlign: "right" }}>
                  <p
                    style={{ cursor: "pointer" }}
                    onClick={() => setShowWarning(false)}
                  >
                    <strong>Close</strong>
                  </p>
                </Col>
              </Col>
            </Row>
          )}

          <PerformanceChartList
            products={products}
            filter={filter}
            showChart={showChart}
            setShowChart={setShowChart}
            setTableSorter={setTableSorter}
            tableSorter={tableSorter}
          />
        </Container>
      </div>

      <Products
        setCurrentSort={setTableSorter}
        filter={filter}
        products={products}
        methodList={methodList}
        geoList={geoList}
        rebalanceList={rebalanceList}
        uiLevels={uiLevels}
        setFilter={setFilter}
      />
    </PageWrap>
  );
};

const PerformanceChartList = ({
  products,
  filter,
  setTableSorter,
  tableSorter,
  showChart,
  setShowChart,
}: PerformanceChartListProps) => {
  const [fieldSelected, setFieldSelected] = useState("perf1");

  const sorterChart = useMemo(() => {
    const fields: any = {
      name: "name",
      rank: "rank",
      oneYearPerf: "perf1",
      three_years_perf: "perf3",
      ten_years_perf: "perf10",
      ten_years_perf_diff: "perf10E",
      sharpeRatio: "sharpe",
      positiveQuartersPerc: "quaPositive",
      outperformersQuartersPerc: "quaPBetter",
    };

    // On sort change
    if (
      fields[tableSorter.field] === "name" ||
      fields[tableSorter.field] === "rank"
    ) {
      setFieldSelected(fields["oneYearPerf"]);
    } else {
      setFieldSelected(fields[tableSorter.field]);
    }

    return {
      field:
        fields[tableSorter.field] === "rank"
          ? "perf1"
          : fields[tableSorter.field],
      dir: tableSorter.dir,
    };
  }, [tableSorter.dir, tableSorter.field]);

  const changeAnalytic = useCallback(
    (value) => {
      setFieldSelected(value);

      const value2Field: any = {
        perf1: "oneYearPerf",
        perf3: "three_years_perf",
        perf10: "ten_years_perf",
        perf10E: "ten_years_perf_diff",
        sharpe: "sharpeRatio",
        quaPositive: "positiveQuartersPerc",
        quaPBetter: "outperformersQuartersPerc",
      };

      setTableSorter({ dir: "desc", field: value2Field[value] });
    },
    [setTableSorter],
  );

  const analytics: any = useMemo(() => {
    return {
      perf1: "Performance 1 Year",
      perf3: "Performance 3 Years",
      perf10E: "Excess return 10 Years",
      perf10: "Performance 10 Years",
      sharpe: "Sharpe ratio",
      quaPositive: "(%) of positive quarters",
      quaPBetter: "(%) of outperformers quarter",
    };
  }, []);

  const options = useMemo(() => {
    const opts: any = [];

    for (const analytic in analytics) {
      opts.push({ name: analytics[analytic], value: analytic });
    }

    return opts;
  }, [analytics]);

  return (
    <>
      <Container>
        <Row className="justify-content-center align-items-center">
          <Col sm={3} className="mb-3 align-self-end text-start"></Col>
          <Col sm={12}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div style={{ display: "flex", gap: "8px" }}>
                {showChart && (
                  <Dropdown>
                    <Dropdown.Toggle variant="light" id="dropdown-basic">
                      {analytics[fieldSelected]}
                    </Dropdown.Toggle>

                    <Dropdown.Menu>
                      {options.map((analytic: any, index: any) => (
                        <Dropdown.Item>
                          <p
                            key={index}
                            className="dropdown-item"
                            onClick={() => changeAnalytic(analytic.value)}
                          >
                            {analytic.name}
                          </p>
                        </Dropdown.Item>
                      ))}
                    </Dropdown.Menu>
                  </Dropdown>
                )}
              </div>
              {showChart && (
                <p
                  className="close-btn-chart"
                  onClick={() => setShowChart(false)}
                >
                  CLOSE
                </p>
              )}
            </div>
          </Col>
        </Row>
      </Container>
      <Container>
        {products != null && showChart && (
          <ProductsChart
            filters={filter}
            sorter={sorterChart}
            analytics={analytics}
            fieldSelected={fieldSelected}
            products={products}
          />
        )}
      </Container>
    </>
  );
};

const HelpRow = ({
  setShowChart,
  showMethodolgyBox,
  showWarning,
  setShowWarning,
  setShowMethodolgyBox,
  showChart,
}: any) => {
  return (
    <>
      <Row className="mb-1">
        <Col lg={12}>
          <Button
            style={{
              textAlign: "left",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "6px 10px",
              fontWeight: 600,
              minWidth: "10vw",
              fontSize: "0.85rem",
            }}
            className={`btn rounded-3 ${showChart ? "btn-primary" : "btn-featured-switcher"}`}
            onClick={() => setShowChart((prev: boolean) => !prev)}
          >
            <i className="bi bi-bar-chart-fill fs-6"></i>{" "}
            <span style={{ marginLeft: "2px" }}>Chart</span>
          </Button>
        </Col>
      </Row>

      <Row className="mb-1">
        <Col lg={12}>
          <Button
            style={{
              textAlign: "left",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "6px 10px",
              fontWeight: 600,
              minWidth: "10vw",
            }}
            className={`btn rounded-3 ${showWarning ? "btn-primary" : "btn-featured-switcher"}`}
            onClick={() =>
              setShowWarning((prevShowWarning: boolean) => !prevShowWarning)
            }
          >
            <i className="bi bi-shield-check fs-6"></i>{" "}
            <span style={{ marginLeft: "2px" }}>Risk Facts</span>
          </Button>
        </Col>
      </Row>

      <Row>
        <Col lg={12}>
          <Button
            style={{
              textAlign: "left",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "6px 10px",
              fontWeight: 600,
              minWidth: "10vw",
            }}
            className={`btn rounded-3 ${
              showMethodolgyBox ? "btn-primary" : "btn-featured-switcher"
            }`}
            onClick={() =>
              setShowMethodolgyBox(
                (prevShowWarning: boolean) => !prevShowWarning,
              )
            }
          >
            <i className="bi bi-book fs-6"></i>{" "}
            <span style={{ marginLeft: "2px" }}>Methodology</span>
          </Button>
        </Col>
      </Row>
    </>
  );
};

const Products = ({
  products,
  filter,
  setCurrentSort,
  methodList,
  geoList,
  uiLevels,
  rebalanceList,
  setFilter,
}: any) => {
  const ref = useRef<any>(undefined);
  const isFeatured =
    filter["uiLevel"].length === 1 && filter["uiLevel"]?.[0] === "primary";

  return (
    <div className="mainContent">
      <div className="mainContent__aside d-none d-md-block">
        <FeaturedSwitch
          options={uiLevels}
          setFilter={setFilter}
          isFeatured={isFeatured}
        />
        <ProductChekboxFilter
          filter={filter}
          setFilter={setFilter}
          options={geoList}
          type={"geo"}
          title="Investment Universe"
        />
        <ProductChekboxFilter
          filter={filter}
          setFilter={setFilter}
          options={methodList}
          type={"method"}
          title="Investment Approach"
        />
        <ProductChekboxFilter
          filter={filter}
          setFilter={setFilter}
          options={rebalanceList}
          type={"rebalance"}
          title={"Rebalance"}
        />
      </div>
      <div className="mainContent__center">
        {products && (
          <ProductListTableItems
            ref={ref}
            hasMarginTop={false}
            setCurrentSort={setCurrentSort}
            filter={filter}
            products={products}
          />
        )}
      </div>
      <div className="mainContent__aside d-none d-md-block">
        <RankSidebar
          handleChangeRank={ref.current?.handleChangeRank}
          handleSwitchAbsPerf={ref.current?.handleSwitchAbsPerf}
        />
      </div>
    </div>
  );
};

type DataSourceRow = Record<"id" | "market" | "sector", string>;
type WeightsStruct = {
  indicator: string;
  weight: number;
  order: "desc" | "asc";
  rel: boolean;
  relIndicator: undefined | string;
}[];

export class RankStrategies {
  dataSource: DataSourceRow[];
  weights: WeightsStruct;
  bestN?: number;

  constructor(source: any, weights?: { [key: string]: number }) {
    this.dataSource = [];

    // Assign a default weights map
    this.weights = [
      {
        indicator: sharpeRatio,
        weight: 0,
        order: "desc",
        rel: false,
        relIndicator: undefined,
      },
      {
        indicator: avgYearlyDrawDown,
        weight: 0,
        order: "desc",
        rel: false,
        relIndicator: undefined,
      },
      {
        indicator: tenYearsReturnH,
        weight: 0,
        order: "desc",
        rel: false,
        relIndicator: tenYearsReturnDiff,
      },
      {
        indicator: threeYearsReturn,
        weight: 0,
        order: "desc",
        rel: false,
        relIndicator: threeYearsReturnDiff,
      },
      {
        indicator: fiveYearsReturn,
        weight: 0,
        order: "desc",
        rel: false,
        relIndicator: fiveYearsReturnDiff,
      },

      {
        indicator: oneYearReturn,
        weight: 0,
        order: "desc",
        rel: false,
        relIndicator: oneYearReturnDiff,
      },
      {
        indicator: ytd,
        weight: 0,
        order: "asc",
        rel: false,
        relIndicator: ytdDiff,
      },
      {
        indicator: turnover,
        weight: 0,
        order: "asc",
        rel: false,
        relIndicator: undefined,
      },
      {
        indicator: "",
        weight: 0,
        order: "asc",
        rel: false,
        relIndicator: undefined,
      },
    ];

    if (weights != null) {
      for (const w of this.weights) {
        if (w.indicator in weights) {
          w.weight = weights[w.indicator];
        }
      }
    }

    this.assignZScoreMatrix(source);
  }

  private assignZScoreMatrix(source: any) {
    this.dataSource = this.prepareDataSource(source);
    this.computeMatrixZScore(this.dataSource, this.getScoreIndicators());
  }

  private getScoreIndicators() {
    const indicatorsWeights: {
      [key: string]: { w: number; order: "desc" | "asc" };
    } = {};

    for (const key in this.weights) {
      indicatorsWeights[this.weights[key].indicator] = {
        w: this.weights[key]["weight"],
        order: this.weights[key]["order"],
      };

      if (this.weights[key].relIndicator !== undefined) {
        indicatorsWeights[this.weights[key].relIndicator!] = {
          w: this.weights[key].weight,
          order: this.weights[key].order,
        };
      }
    }

    return indicatorsWeights;
  }

  private prepareDataSource(source: any) {
    const ds: DataSourceRow[] = [];

    for (const row of source) {
      const obj: any = JSON.parse(JSON.stringify(row));

      for (const key in row["analytics"]) {
        obj[key] = { value: row["analytics"][key], zScore: -3 };
      }

      obj["id"] = row["id"];
      obj["market"] = row["market"];
      obj["sector"] = row["sector"];
      obj["name"] = row["name"];
      obj["synteticId"] = row["tagId"];

      ds.push(obj);
    }

    return ds;
  }

  private stdDev(values: number[], mean: number) {
    const n = values.length;
    if (n === 0) return 0;

    const variance =
      values.reduce((sum, val) => {
        if (typeof val !== "number" || isNaN(val)) {
          return sum;
        }

        return sum + Math.pow(val - mean, 2);
      }, 0) / n;
    return Math.sqrt(variance);
  }

  private mean(values: number[]) {
    const N = values.length;
    let sum = 0;

    for (const v of values) {
      sum += v;
    }

    return sum / N;
  }

  private zScore(x: number, avg: number, stdDev: number) {
    if (stdDev === 0) {
      // all values are equals
      return 0;
    }

    const z = (x - avg) / stdDev;

    if (z > 3) {
      return 3;
    }

    if (z < -3) {
      return -3;
    }

    return z;
  }

  private computeMatrixZScore(
    population: any,
    weights: { [key: string]: { w: number; order: "desc" | "asc" } },
  ) {
    const indicators: string[] = [];

    // Extract the indicators
    for (const key in population[0]) {
      if (key !== "id" && key !== "market" && key !== "sector") {
        if (key in weights) {
          indicators.push(key);
        }
      }
    }

    let row: any = null;
    let idx = 0;
    let order = "desc";
    let zScore: any = null;
    let values: any = [];
    let dictValue: any = {};
    let avg = 0;
    let stdDev = 0;

    for (const indicator of indicators) {
      idx = 0;
      values = [];
      dictValue = {};
      order = weights[indicator].order;

      for (let j = 0, N = population.length; j < N; j++) {
        row = population[j];

        if (row[indicator] !== null && row[indicator] !== undefined) {
          values.push(row[indicator].value);
          dictValue[idx] = j;

          idx++;
        }
      }

      avg = this.mean(values);
      stdDev = this.stdDev(values, avg);

      zScore = this.zScores(values, avg, stdDev);

      if (order === "asc") {
        for (let i = 0, N = zScore.length; i < N; i++) {
          zScore[i] *= -1;
        }
      }

      for (let i = 0, N = zScore.length; i < N; i++) {
        idx = dictValue[i];
        population[idx][indicator]["zScore"] = zScore[i];
      }
    }
  }

  private zScores(values: any, mean: any, stdDev: any) {
    const zScores: any = [];

    for (const v of values) {
      zScores.push(this.zScore(v, mean, stdDev));
    }

    return zScores;
  }

  private weightedMean(
    values: { [key: string]: number },
    weights: { [key: string]: number },
  ) {
    let weightsSum = 0;
    let sum = 0;
    let v = 0;
    let w = 0;

    for (const key in weights) {
      weightsSum += weights[key];
    }

    for (const key in values) {
      v = values[key];
      w = weights[key];
      sum += v * w;
    }

    return sum / weightsSum;
  }

  private groupBestByMarketSector(data: any) {
    const grouped: any = {};

    data.forEach((item: any) => {
      const key = `${item.market}|${item.sector}`;
      if (!grouped[key]) {
        grouped[key] = [];
      }
      grouped[key].push(item);
    });

    return grouped;
  }

  byZScore() {
    const weightsIndicators: { id: string; w: number }[] = [];
    let useRelative = false;
    let relIndicator = "";

    for (const w of this.weights) {
      if (w.weight > 0) {
        useRelative = w.rel;
        relIndicator = w.relIndicator ?? "";
        weightsIndicators.push({
          w: w.weight,
          id: useRelative ? relIndicator : w.indicator,
        });
      }
    }

    let zScore = 0;

    for (const r of this.dataSource as any) {
      const v: any = {};
      const w: any = {};

      for (const i of weightsIndicators) {
        if (i.id in r) {
          v[i.id] = r[i.id]["zScore"];
          w[i.id] = i.w;
        }
      }

      if (Object.keys(v).length > 0) {
        zScore = this.weightedMean(v, w);

        r["zScore"] = zScore;
      } else {
        r["zScore"] = -3;
      }
    }

    this.dataSource.sort((a: any, b: any) =>
      a["zScore"] > b["zScore"] ? -1 : 1,
    );
    for (let i = 0, N = this.dataSource.length; i < N; i++) {
      if (!weightsIndicators || weightsIndicators.length === 0) {
        (this.dataSource as any)[i]["rank"] = null;
      } else {
        (this.dataSource as any)[i]["rank"] = i + 1;
      }
    }

    return this.clusterByMarketSector();
  }

  // setBestBy(value) {
  //   this.bestN = value;
  // }

  private clusterByMarketSector() {
    const grouped = this.groupBestByMarketSector(this.dataSource);

    const result: any[] = [];

    for (const key in grouped) {
      if (this.bestN !== undefined) {
        grouped[key].slice(0, this.bestN).forEach((i: any) => result.push(i));
      } else {
        grouped[key].forEach((i: any) => result.push(i));
      }
    }

    result.sort((a, b) => (a["rank"] > b["rank"] ? 1 : -1));

    return result;
  }

  updateWeights(weightsMap: any) {
    for (const i in weightsMap) {
      for (const obj of this.weights) {
        if (i === obj.indicator) {
          obj.weight = weightsMap[i];

          break;
        }
      }
    }

    return this.byZScore();
  }

  switchWeightsPerf(indicator: string, useRelative: boolean) {
    for (const wObj of this.weights) {
      if (indicator === wObj.indicator) {
        wObj.rel = useRelative;

        break;
      }
    }

    return this.byZScore();
  }

  // filterStrategies(filteredSource) {
  //   this.assignZScoreMatrix(filteredSource);
  //   return this.byZScore();
  // }

  getActiveRankingIndicators() {
    const fields: string[] = [];

    for (const w of this.weights) {
      if (w.weight > 0) {
        if (w.rel) {
          if (w.relIndicator !== undefined) {
            fields.push(w.relIndicator);
          }
        } else {
          fields.push(w.indicator);
        }
      }
    }
    return fields;
  }
}

const FeaturedSwitch = ({ options, setFilter, isFeatured }: any) => {
  const toggleSwitch = (value: boolean) => {
    setFilter((f: any) => {
      if (value === true) {
        return { ...f, uiLevel: ["primary"] };
      } else {
        return { ...f, uiLevel: ["primary", "extended"] };
      }
    });
  };

  return (
    <Stack
      direction="horizontal"
      gap={3}
      className="justify-content-end align-items-center mb-1"
    >
      <div style={{ display: "flex", flexWrap: "wrap", flex: 1, gap: "4px" }}>
        {options.map((opt: string) => {
          if (opt === "primary") {
            return (
              <Button
                style={{
                  justifySelf: "flex-start",
                  flex: 1,
                  minWidth: "140px",
                }}
                className={`btn-featured-switcher ${isFeatured ? "btn-featured-switcher__btn-active" : ""}`}
                onClick={() => toggleSwitch(true)}
              >
                <span>SELECTION</span>
              </Button>
            );
          } else if (opt === "extended") {
            return (
              <Button
                style={{
                  justifySelf: "flex-start",
                  flex: 1,
                  minWidth: "140px",
                }}
                className={`btn-featured-switcher ${!isFeatured ? "btn-featured-switcher__btn-active" : ""}`}
                onClick={() => toggleSwitch(false)}
              >
                <span>ALL PORTFOLIOS</span>
              </Button>
            );
          }

          return <></>;
        })}
      </div>
    </Stack>
  );
};
